import { Button, Icon, Image } from '@salecycle/ui-component-library';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useHistory, useParams } from 'react-router-dom';
import { BulkRetargetingRoutes } from 'sections/bulk-retargeting-campaigns/routes';
import styled, { useTheme } from 'styled-components';
import { CampaignNameInput } from './CampaignNameInput';
import useClient from 'core/ui/utils/useClient';
import { useSaveCampaign } from '../hooks/useSaveCampaign';
import { T } from '@salecycle/experimental/Typography/Typography';
import { useDrawer } from '../state/actions/useDrawer';
import { OffsiteCampaignStatus } from 'models/offsite/Campaign/LifeCycle';
import { useCampaignEditorContext } from '../state/hooks/useCampaignEditorContext';

const ContentElement = styled.div`
  background-color: ${({ theme }) => theme.vars.newColor.darkGS1};
  padding: 9px 40px 10px 10px;
  width: 100%;
  @media (max-width: 768px) {
    padding: 16px;
  }
  @media (min-width: 768px) {
    display: grid;
    grid-template-areas: 'leftSide centerSide rightSide';
    grid-template-columns: 25% 50% 25%;
  }
`;

export const LeftSide = styled.div`
  display: flex;
  grid-area: leftSide;
  align-items: center;
  button {
    @media (max-width: 768px) {
      width: 100%;
    }
  }
  svg {
    transform: rotate(180deg);
  }
`;

export const LogoBox = styled.div`
  height: 50px;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CenterContent = styled.div`
  grid-area: centerSide;
  display: flex;
  justify-content: center;
  @media (max-width: 768px) {
    margin: 10px 0;
  }
`;

export const RightSide = styled.div`
  display: flex;
  grid-area: rightSide;
  align-items: center;
  justify-content: end;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
  @media (min-width: 768px) {
    gap: 16px;
  }
  button {
    @media (max-width: 768px) {
      width: 100%;
      :hover {
        margin-bottom: 10px;
      }
    }
  }
`;

export interface CampaignHeaderBarProps {
  isError: boolean;
}

export const CampaignHeaderBar = ({ isError }: CampaignHeaderBarProps) => {
  const { t } = useTranslation('campaigns');
  const { siteId } = useParams<{ siteId: string }>();
  const history = useHistory();
  const theme = useTheme();
  const { state } = useCampaignEditorContext();

  const client = useClient();

  const { saveCampaign } = useSaveCampaign();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setDrawer] = useDrawer();

  const onExitButtonClicked = useCallback(() => {
    history.push(generatePath(BulkRetargetingRoutes.LIST, { siteId }));
  }, [history, siteId]);

  return (
    <ContentElement>
      <LeftSide>
        <LogoBox>
          <Link to={`/`}>
            <Image src="/images/slim-logo.svg" alt="SaleCycle Logo" />
          </Link>
        </LogoBox>
        <Button
          onClick={onExitButtonClicked}
          variation="secondaryDark"
          leftSideIcon={<Icon icon="arrow-right" />}
          title={t('bulkRetargetingCampaigns.editor.exitCampaignEditor')}
          width="180px"
        />
      </LeftSide>
      <CenterContent>
        <CampaignNameInput />
      </CenterContent>

      {!isError && (
        <RightSide>
          <T style={{ color: theme.vars.newColor.white }}>
            <span aria-label="Client name" style={{ fontWeight: 'bold', paddingRight: '12px' }}>
              {t('onsite.campaignStatus.clientLabel')}
              {client?.name}
            </span>
          </T>
          {!state.readOnly && (
            <>
              <Button
                onClick={saveCampaign}
                ariaLabel={t('bulkRetargetingCampaigns.editor.saveCampaignAria')}
                variation="secondary"
                disabled={!state.hasUnsavedChanges}
              >
                {t('bulkRetargetingCampaigns.editor.saveCampaign')}
              </Button>
              <Button
                onClick={() => setDrawer(true)}
                ariaLabel={t('bulkRetargetingCampaigns.editor.reviewAndSendAria')}
                variation="primary"
              >
                {t('bulkRetargetingCampaigns.editor.reviewAndSend')}
              </Button>
            </>
          )}
          {state.status === OffsiteCampaignStatus.Scheduled && (
            <Button
              onClick={() => setDrawer(true)}
              ariaLabel={t('bulkRetargetingCampaigns.editor.unschedule')}
              variation="primary"
            >
              {t('bulkRetargetingCampaigns.editor.unschedule')}
            </Button>
          )}
        </RightSide>
      )}
    </ContentElement>
  );
};
